import React, { useState, useMemo } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS_DOM = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const STATUS_OPTIONS = ['programada', 'confirmada', 'completada', 'cancelada', 'no-show'];
const TIPOS_BASE = ['consulta', 'revision', 'tratamiento', 'control', 'procedimiento', 'seguimiento'];
const BLOCK_REASONS = ['Vacaciones', 'Feriado', 'Cerrado por la clínica'];

const STATUS_CLS = {
  programada: 'border-emerald-300 bg-emerald-50',
  confirmada: 'border-sky-300 bg-sky-50',
  completada: 'border-slate-200 bg-slate-50 opacity-70',
  cancelada: 'border-red-200 bg-red-50 opacity-60 line-through',
  'no-show': 'border-orange-300 bg-orange-50',
};

const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfWeekSun = (date) => { const d = new Date(date); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d; };
const fmtTime = (dt) => dt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
const monthTitle = (d) => `${MESES[d.getMonth()]} ${d.getFullYear()}`;
const defaultFetch = (url, opts = {}) => fetch(url, { credentials: 'include', ...opts });

const buildByDate = (appointments) => {
  const map = {};
  appointments.forEach((a) => {
    if (!a.scheduled_at) return;
    const dt = new Date(a.scheduled_at);
    if (Number.isNaN(dt.getTime())) return;
    const key = dateKey(dt);
    (map[key] = map[key] || []).push({ ...a, _dt: dt });
  });
  Object.values(map).forEach((l) => l.sort((x, y) => x._dt - y._dt));
  return map;
};

const CitaForm = ({ initialDate, withDate, onCreate, saving, apiFetch, onDone }) => {
  const [form, setForm] = useState({ patient_name: '', date: initialDate || dateKey(new Date()), time: '12:00', appointment_type: 'consulta', status: 'programada', notes: '' });
  const [suggestions, setSuggestions] = useState([]);
  const search = async (term) => {
    if (term.trim().length < 2) { setSuggestions([]); return; }
    try {
      const res = await apiFetch(`${API}/clinic/patients/search?q=${encodeURIComponent(term.trim())}`);
      if (res.ok) setSuggestions((await res.json()).patients || []);
    } catch { setSuggestions([]); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) return;
    const ok = await onCreate({
      patient_name: form.patient_name.trim(), patient_phone: '', patient_email: '',
      appointment_type: form.appointment_type, scheduled_at: `${form.date}T${form.time}`,
      assigned_role: 'compartida', status: form.status, notes: form.notes || 'Creada desde el calendario interactivo',
    });
    if (ok) { setForm((p) => ({ ...p, patient_name: '', notes: '' })); onDone && onDone(); }
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-2xl border border-[#0f766e]/30 bg-[#0f766e]/5 p-4" data-testid="calendar-cita-form">
      <p className="text-sm font-black text-slate-900">➕ Nueva cita</p>
      <input value={form.patient_name} list="crm-cal-suggestions" onChange={(e) => { setForm((p) => ({ ...p, patient_name: e.target.value })); search(e.target.value); }} placeholder="Paciente (sugiere del CRM)" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="calendar-cita-patient" required />
      <datalist id="crm-cal-suggestions">{suggestions.map((s) => <option key={s.patient_name} value={s.patient_name} />)}</datalist>
      <div className="flex flex-wrap gap-2">
        {withDate && <input type="date" lang="es" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="flex-1 min-w-[130px] rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 [color-scheme:light]" data-testid="calendar-cita-date" required />}
        <input type="time" value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} className="flex-1 min-w-[90px] rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="calendar-cita-time" />
        <select value={form.appointment_type} onChange={(e) => setForm((p) => ({ ...p, appointment_type: e.target.value }))} className="flex-1 min-w-[110px] rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="calendar-cita-type">
          {TIPOS_BASE.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Notas (opcional)" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="calendar-cita-notes" />
      <button type="submit" disabled={saving} className="w-full rounded-xl bg-[#0f766e] py-2.5 text-sm font-black text-white disabled:opacity-50" data-testid="calendar-cita-save">{saving ? 'Guardando…' : 'Guardar cita'}</button>
    </form>
  );
};

const Modal = ({ children, onClose, testId, maxW = 'max-w-md' }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4" onClick={onClose} data-testid={testId}>
    <div className={`w-full ${maxW} max-h-[92vh] sm:max-h-[85vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white p-4 sm:p-6 shadow-2xl`} onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

export default function InteractiveCalendar({ appointments = [], onCreate, onUpdate, onDelete, canEdit = false, saving = false, apiFetch = defaultFetch, subtitle = 'Calendario interactivo · CRM · Aynara' }) {
  const [view, setView] = useState('mensual');
  const [anchor, setAnchor] = useState(() => new Date());
  const [fPro, setFPro] = useState('todos');
  const [fTipo, setFTipo] = useState('todos');
  const [fEstado, setFEstado] = useState('todos');
  const [dayModal, setDayModal] = useState(null);
  const [showCita, setShowCita] = useState(false);
  const [showPatient, setShowPatient] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [remFor, setRemFor] = useState(null);
  const [remForm, setRemForm] = useState({ date: '', time: '10:00', message: '' });
  const [remMsg, setRemMsg] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ patient_name: '', time: '12:00', status: 'programada' });
  const [crmInfo, setCrmInfo] = useState(null);
  const [blocked, setBlocked] = useState({});
  const [blockReason, setBlockReason] = useState(BLOCK_REASONS[0]);
  const today = new Date();

  const loadBlocked = React.useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/clinic/blocked-days`);
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data.blocked_days || []).forEach((b) => { map[b.date] = b; });
        setBlocked(map);
      }
    } catch { /* noop */ }
  }, [apiFetch]);
  React.useEffect(() => { loadBlocked(); }, [loadBlocked]);

  const blockDay = async (date) => {
    try {
      const res = await apiFetch(`${API}/clinic/blocked-days`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date, reason: blockReason }) });
      if (res.ok) loadBlocked();
    } catch { /* noop */ }
  };
  const unblockDay = async (id) => {
    try {
      const res = await apiFetch(`${API}/clinic/blocked-days/${id}`, { method: 'DELETE' });
      if (res.ok) loadBlocked();
    } catch { /* noop */ }
  };

  const proOptions = useMemo(() => [...new Set(appointments.map((a) => a.assigned_role).filter(Boolean))].sort(), [appointments]);
  const tipoOptions = useMemo(() => [...new Set(appointments.map((a) => a.appointment_type).filter(Boolean))].sort().slice(0, 40), [appointments]);

  const filtered = useMemo(() => appointments.filter((a) =>
    (fPro === 'todos' || a.assigned_role === fPro) &&
    (fTipo === 'todos' || a.appointment_type === fTipo) &&
    (fEstado === 'todos' || a.status === fEstado)
  ), [appointments, fPro, fTipo, fEstado]);

  const byDate = useMemo(() => buildByDate(filtered), [filtered]);

  const monthCells = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = startOfWeekSun(first);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);

  const weekDays = useMemo(() => {
    const start = startOfWeekSun(anchor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);

  const prevMonth = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
  const nextMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  const move = (dir) => setAnchor((prev) => {
    const d = new Date(prev);
    if (view === 'mensual') d.setMonth(d.getMonth() + dir);
    else if (view === 'semanal') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    return d;
  });

  const openDay = (key) => { setDayModal(key); setEditId(null); setCrmInfo(null); };

  const fetchCrm = async (cita) => {
    try {
      const res = await apiFetch(`${API}/clinic/patients/search?q=${encodeURIComponent(cita.patient_name.slice(0, 40))}`);
      const data = res.ok ? await res.json() : { patients: [] };
      setCrmInfo({ citaId: cita.id, patient: (data.patients || [])[0] || null });
    } catch { setCrmInfo({ citaId: cita.id, patient: null }); }
  };

  const openReminderForm = (a) => {
    const d = new Date(a._dt);
    d.setDate(d.getDate() - 1);
    const base = d < new Date() ? new Date() : d;
    setRemForm({ date: dateKey(base), time: '10:00', message: '' });
    setRemMsg(null);
    setRemFor(a.id);
  };

  const scheduleReminder = async (a) => {
    try {
      const res = await apiFetch(`${API}/clinic/scheduled-reminders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointment_id: a.id, send_at: `${remForm.date}T${remForm.time}`, message: remForm.message }),
      });
      const data = await res.json();
      if (res.ok) {
        setRemMsg({ id: a.id, ok: true, text: data.has_email ? '✅ Recordatorio programado (email automático).' : '✅ Programado. Sin email en ficha: llegará aviso interno para enviar WhatsApp manual.' });
        setRemFor(null);
      } else setRemMsg({ id: a.id, ok: false, text: data.detail || 'No se pudo programar.' });
    } catch { setRemMsg({ id: a.id, ok: false, text: 'Error de conexión.' }); }
  };

  const dayList = byDate[dayModal] || [];
  const anchorKey = dateKey(anchor);
  const anchorList = byDate[anchorKey] || [];

  const CitaCard = ({ a, showActions = true }) => (
    <div className={`rounded-2xl border px-3 py-2.5 ${STATUS_CLS[a.status] || 'border-slate-200 bg-slate-50'}`} data-testid={`cal-cita-${a.id}`}>
      <div className="flex items-center gap-3">
        <span className="rounded-xl bg-slate-900 px-2.5 py-1.5 text-sm font-black text-white whitespace-nowrap">{fmtTime(a._dt)}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 leading-tight">{a.patient_name}</p>
          <p className="text-xs text-slate-700 capitalize">{a.appointment_type || 'cita'} · {a.status}{a.assigned_role && a.assigned_role !== 'compartida' ? ` · ${a.assigned_role}` : ''}</p>
          {a.notes && <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">{a.notes}</p>}
        </div>
      </div>
      {crmInfo?.citaId === a.id && (
        <div className="mt-2 rounded-xl bg-white/80 border border-slate-200 px-3 py-2 text-xs text-slate-700" data-testid={`cal-crm-info-${a.id}`}>
          {crmInfo.patient ? (
            <>
              <p className="font-bold text-slate-900">📇 {crmInfo.patient.patient_name}</p>
              <p>📞 {crmInfo.patient.patient_phone || 'sin teléfono'} {crmInfo.patient.patient_email ? `· ✉️ ${crmInfo.patient.patient_email}` : ''}</p>
              {crmInfo.patient.latest_annotation && <p className="mt-0.5">Último: {crmInfo.patient.latest_annotation}</p>}
              {crmInfo.patient.total_sessions ? <p>Sesiones históricas: {crmInfo.patient.total_sessions}</p> : null}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {!!crmInfo.patient.patient_phone && (
                  <a href={`https://wa.me/${(crmInfo.patient.patient_phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${crmInfo.patient.patient_name}, le escribimos de la Clínica de la Dra. Sandra Alvarez para recordarle su cita del ${a._dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} a las ${fmtTime(a._dt)}.`)}`} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-100 px-3 py-1 font-bold text-emerald-800 hover:bg-emerald-200" data-testid={`cal-wa-${a.id}`}>💬 WhatsApp</a>
                )}
                {!!crmInfo.patient.patient_email && (
                  <a href={`mailto:${crmInfo.patient.patient_email}?subject=${encodeURIComponent('Su cita en la Clínica Dra. Sandra Alvarez')}&body=${encodeURIComponent(`Hola ${crmInfo.patient.patient_name}, le recordamos su cita del ${a._dt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} a las ${fmtTime(a._dt)}.`)}`} className="rounded-full bg-sky-100 px-3 py-1 font-bold text-sky-800 hover:bg-sky-200" data-testid={`cal-email-${a.id}`}>✉️ Email</a>
                )}
              </div>
            </>
          ) : <p>Sin ficha CRM registrada todavía para este nombre.</p>}
        </div>
      )}
      {showActions && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" onClick={() => crmInfo?.citaId === a.id ? setCrmInfo(null) : fetchCrm(a)} className="rounded-full bg-sky-100 px-3 py-1 text-[11px] font-bold text-sky-800" data-testid={`cal-crm-${a.id}`}>📇 CRM</button>
          {canEdit && editId !== a.id && (
            <>
              <button type="button" onClick={() => { setEditId(a.id); setEdit({ patient_name: a.patient_name, time: a._dt.toTimeString().slice(0, 5), status: a.status }); }} className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-bold text-white" data-testid={`cal-editar-${a.id}`}>✏️ Editar</button>
              <button type="button" onClick={() => remFor === a.id ? setRemFor(null) : openReminderForm(a)} className="rounded-full bg-violet-100 px-3 py-1 text-[11px] font-bold text-violet-800" data-testid={`cal-recordatorio-${a.id}`}>⏰ Recordatorio</button>
              {a.status !== 'cancelada' && <button type="button" disabled={!!saving} onClick={() => onUpdate && onUpdate(a.id, { status: 'cancelada' })} className="rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-800" data-testid={`cal-cancelar-${a.id}`}>Cancelar</button>}
              <button type="button" disabled={!!saving} onClick={() => window.confirm(`¿Eliminar la cita de ${a.patient_name}?`) && onDelete && onDelete(a.id)} className="rounded-full bg-rose-100 px-3 py-1 text-[11px] font-bold text-rose-700" data-testid={`cal-eliminar-${a.id}`}>🗑</button>
            </>
          )}
        </div>
      )}
      {canEdit && remFor === a.id && (
        <form className="mt-2 space-y-1.5 rounded-xl border border-violet-200 bg-violet-50 p-2.5" onSubmit={(e) => { e.preventDefault(); scheduleReminder(a); }} data-testid={`cal-rem-form-${a.id}`}>
          <p className="text-[11px] font-black text-violet-900">⏰ Programar recordatorio · elige día y hora de envío</p>
          <div className="flex flex-wrap gap-1.5">
            <input type="date" lang="es" value={remForm.date} onChange={(e) => setRemForm((p) => ({ ...p, date: e.target.value }))} className="flex-1 min-w-[120px] rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 [color-scheme:light]" data-testid={`cal-rem-date-${a.id}`} required />
            <input type="time" value={remForm.time} onChange={(e) => setRemForm((p) => ({ ...p, time: e.target.value }))} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 [color-scheme:light]" data-testid={`cal-rem-time-${a.id}`} required />
          </div>
          <input value={remForm.message} onChange={(e) => setRemForm((p) => ({ ...p, message: e.target.value }))} placeholder="Mensaje personalizado (opcional)" className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs" data-testid={`cal-rem-msg-${a.id}`} />
          <div className="flex gap-1.5">
            <button type="submit" className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-black text-white" data-testid={`cal-rem-save-${a.id}`}>Programar</button>
            <button type="button" onClick={() => setRemFor(null)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs">✕</button>
          </div>
        </form>
      )}
      {remMsg?.id === a.id && <p className={`mt-2 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${remMsg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`} data-testid={`cal-rem-result-${a.id}`}>{remMsg.text}</p>}
      {canEdit && editId === a.id && (
        <form className="mt-2 flex flex-wrap gap-1.5" onSubmit={async (e) => { e.preventDefault(); const ok = await onUpdate(a.id, { patient_name: edit.patient_name.trim(), scheduled_at: `${dateKey(a._dt)}T${edit.time}`, status: edit.status }); if (ok) setEditId(null); }} data-testid={`cal-edit-form-${a.id}`}>
          <input value={edit.patient_name} onChange={(e) => setEdit((p) => ({ ...p, patient_name: e.target.value }))} className="flex-1 min-w-[120px] rounded-lg border border-slate-300 px-2 py-1.5 text-xs" required />
          <input type="time" value={edit.time} onChange={(e) => setEdit((p) => ({ ...p, time: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" />
          <select value={edit.status} onChange={(e) => setEdit((p) => ({ ...p, status: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="submit" disabled={!!saving} className="rounded-lg bg-[#0f766e] px-3 py-1.5 text-xs font-bold text-white" data-testid={`cal-edit-save-${a.id}`}>Guardar</button>
          <button type="button" onClick={() => setEditId(null)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs">✕</button>
        </form>
      )}
    </div>
  );

  return (
    <section className="rounded-[28px] border border-white/60 bg-white/95 p-4 sm:p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="interactive-calendar">
      {/* Header navegación */}
      <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">{subtitle}</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase text-slate-500">Ir al día →</label>
          <input type="date" lang="es" value={anchorKey} onChange={(e) => { if (e.target.value) { setAnchor(new Date(`${e.target.value}T12:00`)); } }} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 [color-scheme:light]" data-testid="calendar-goto-date" />
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button type="button" onClick={() => setAnchor(prevMonth)} className="hidden sm:block text-sm font-semibold text-slate-400 capitalize hover:text-slate-700" data-testid="calendar-prev-month-label">{monthTitle(prevMonth)}</button>
          <button type="button" onClick={() => move(-1)} className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-[#1d7fd6] text-lg font-black text-white shadow hover:bg-[#1668b0] active:scale-95" data-testid="calendar-prev">‹</button>
          <h2 className="text-xl sm:text-3xl font-black capitalize text-[#1d4ed8]" data-testid="calendar-title">{view === 'mensual' ? monthTitle(anchor) : anchor.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</h2>
          <button type="button" onClick={() => move(1)} className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-[#1d7fd6] text-lg font-black text-white shadow hover:bg-[#1668b0] active:scale-95" data-testid="calendar-next">›</button>
          <button type="button" onClick={() => setAnchor(nextMonth)} className="hidden sm:block text-sm font-semibold text-slate-400 capitalize hover:text-slate-700" data-testid="calendar-next-month-label">{monthTitle(nextMonth)}</button>
        </div>
        <button type="button" onClick={() => setAnchor(new Date())} className="rounded-xl border-2 border-slate-800 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-black text-slate-900 shadow hover:bg-slate-100 active:scale-95" data-testid="calendar-today">IR a HOY 📅</button>
      </div>

      {/* Acciones + filtros */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {canEdit && <button type="button" onClick={() => setShowPatient(true)} className="rounded-full bg-[#1d4ed8] px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-white shadow hover:bg-[#1e40af] active:scale-95" data-testid="calendar-add-patient">⊕ Paciente</button>}
        {canEdit && <button type="button" onClick={() => setShowCita(true)} className="rounded-full bg-[#0f766e] px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-white shadow hover:bg-[#0d5f58] active:scale-95" data-testid="calendar-add-cita">⊕ Cita</button>}
        <select value={fTipo} onChange={(e) => setFTipo(e.target.value)} className="max-w-[46vw] sm:max-w-none rounded-xl border border-slate-300 bg-white px-2 sm:px-3 py-2 text-xs sm:text-sm text-slate-700" data-testid="calendar-filter-type">
          <option value="todos">VER Citas por TIPO/SALA</option>
          {tipoOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={fPro} onChange={(e) => setFPro(e.target.value)} className="max-w-[46vw] sm:max-w-none rounded-xl border border-slate-300 bg-white px-2 sm:px-3 py-2 text-xs sm:text-sm text-slate-700" data-testid="calendar-filter-professional">
          <option value="todos">VER Citas por PROFESIONAL</option>
          {proOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={fEstado} onChange={(e) => setFEstado(e.target.value)} className="max-w-[46vw] sm:max-w-none rounded-xl border border-slate-300 bg-white px-2 sm:px-3 py-2 text-xs sm:text-sm text-slate-700" data-testid="calendar-filter-status">
          <option value="todos">Estado: todos</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" onClick={() => setShowSearch(true)} className="rounded-full bg-[#ea7317] px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-white shadow hover:bg-[#c95f10] active:scale-95" data-testid="calendar-search-btn">🔍 Consultar Citas</button>
        <button type="button" onClick={() => setShowGoogle(true)} className="rounded-full border border-slate-300 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 hover:border-[#0f766e]" data-testid="calendar-google-btn">🗓 Google Calendar</button>
        {canEdit && <button type="button" onClick={() => setShowReminders(true)} className="rounded-full border border-violet-300 bg-violet-50 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold text-violet-800 hover:border-violet-500" data-testid="calendar-reminders-btn">⏰ Recordatorios</button>}
      </div>

      {/* Toggle de vistas */}
      <div className="mt-4 flex justify-center">
        <div className="grid w-full grid-cols-4 sm:flex sm:w-auto rounded-xl border border-slate-300 overflow-hidden">
          {[['diaria', 'Diaria'], ['horaria', 'Horaria'], ['semanal', 'Semanal'], ['mensual', 'Mensual']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setView(id)} className={`px-2 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm font-bold ${view === id ? 'bg-[#1d7fd6] text-white' : 'text-slate-500 hover:bg-slate-50'}`} data-testid={`calendar-view-${id}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* VISTA MENSUAL */}
      {view === 'mensual' && (
        <div className="mt-5">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DIAS_DOM.map((d) => <p key={d} className="text-center text-[11px] font-bold lowercase text-slate-500">{d}</p>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-month-grid">
            {monthCells.map((d, i) => {
              const key = dateKey(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const isToday = sameDay(d, today);
              const list = byDate[key] || [];
              const blk = blocked[key];
              return (
                <div key={`${key}-${i}`} onClick={() => openDay(key)}
                  className={`relative rounded-lg border p-1 min-h-[52px] sm:min-h-[86px] cursor-pointer transition-shadow hover:border-[#1d7fd6] hover:shadow-md active:scale-[0.97] ${blk ? 'border-amber-300 bg-amber-50' : isToday ? 'border-[#1d7fd6] ring-2 ring-[#1d7fd6]/30 bg-[#eef6ff]' : inMonth ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/70'}`}
                  data-testid={`cal-day-${key}`}>
                  {list.length > 0 && (
                    <span className="absolute top-1 left-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#d94a1f] px-1 text-[10px] font-black text-white shadow" data-testid={`cal-badge-${key}`}>{list.length}</span>
                  )}
                  <p className={`text-right text-xs font-bold ${isToday ? 'text-[#1d4ed8]' : inMonth ? 'text-slate-700' : 'text-slate-300'}`}>{d.getDate()}</p>
                  {blk && <p className="truncate rounded bg-amber-200/80 px-1 text-[9px] font-black text-amber-900" data-testid={`cal-blocked-${key}`}>🚫 {blk.reason}</p>}
                  <div className="mt-1 hidden sm:block space-y-0.5">
                    {list.slice(0, 2).map((a) => (
                      <p key={a.id} className="truncate rounded bg-slate-100 px-1 text-[9px] leading-tight text-slate-700"><span className="font-black">{fmtTime(a._dt)}</span> {a.patient_name}</p>
                    ))}
                    {list.length > 2 && <p className="text-[9px] font-bold text-slate-400">+{list.length - 2} más</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VISTA SEMANAL */}
      {view === 'semanal' && (
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="calendar-week-grid">
          {weekDays.map((d) => {
            const key = dateKey(d);
            const isToday = sameDay(d, today);
            const list = byDate[key] || [];
            return (
              <div key={key} onClick={() => openDay(key)} className={`rounded-2xl border p-3 min-h-[110px] cursor-pointer hover:border-[#1d7fd6] ${blocked[key] ? 'border-amber-300 bg-amber-50' : isToday ? 'border-[#1d7fd6] bg-[#eef6ff] ring-2 ring-[#1d7fd6]/30' : 'border-slate-200 bg-white'}`} data-testid={`cal-week-day-${key}`}>
                <p className={`text-xs font-black uppercase ${isToday ? 'text-[#1d4ed8]' : 'text-slate-700'}`}>{DIAS_DOM[d.getDay()]} {d.getDate()}{isToday && ' · HOY'}</p>
                {blocked[key] && <p className="mt-1 rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-black text-amber-900">🚫 {blocked[key].reason}</p>}
                <div className="mt-2 space-y-1">
                  {list.slice(0, 6).map((a) => (
                    <p key={a.id} className={`truncate rounded-md border px-1.5 py-0.5 text-[10px] text-slate-800 ${STATUS_CLS[a.status] || 'border-slate-200 bg-slate-50'}`}><span className="font-black">{fmtTime(a._dt)}</span> {a.patient_name}</p>
                  ))}
                  {list.length > 6 && <p className="text-[10px] font-bold text-slate-400">+{list.length - 6} más</p>}
                  {!list.length && <p className="text-[11px] text-slate-400">Sin citas</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* VISTA DIARIA */}
      {view === 'diaria' && (
        <div className="mt-5 space-y-2" data-testid="calendar-day-view">
          <p className="text-sm font-bold text-slate-700">{anchorList.length} citas este día</p>
          {blocked[anchorKey] && <p className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-black text-amber-900">🚫 Día bloqueado · {blocked[anchorKey].reason}</p>}
          {anchorList.map((a) => <CitaCard key={a.id} a={a} />)}
          {!anchorList.length && <p className="rounded-2xl bg-slate-50 p-5 text-center text-sm text-slate-500">{blocked[anchorKey] ? 'Día cerrado, sin citas.' : 'Día libre, sin citas.'}</p>}
          {canEdit && !blocked[anchorKey] && <CitaForm initialDate={anchorKey} withDate={false} onCreate={onCreate} saving={saving} apiFetch={apiFetch} />}
        </div>
      )}

      {/* VISTA HORARIA */}
      {view === 'horaria' && (
        <div className="mt-5 divide-y divide-slate-100 rounded-2xl border border-slate-200" data-testid="calendar-hour-view">
          {Array.from({ length: 14 }, (_, i) => i + 7).map((h) => {
            const slot = anchorList.filter((a) => a._dt.getHours() === h);
            return (
              <div key={h} className="flex items-stretch">
                <div className="w-16 shrink-0 border-r border-slate-100 py-2 pr-2 text-right text-xs font-black text-slate-500">{h <= 12 ? `${h} ${h === 12 ? 'pm' : 'am'}` : `${h - 12} pm`}</div>
                <div className="flex-1 space-y-1 p-1.5">
                  {slot.map((a) => (
                    <div key={a.id} className={`rounded-lg border px-2 py-1 ${STATUS_CLS[a.status] || 'border-slate-200 bg-slate-50'}`}>
                      <p className="text-xs text-slate-900"><span className="font-black">{fmtTime(a._dt)}</span> · <span className="font-bold">{a.patient_name}</span> <span className="capitalize text-slate-500">{a.appointment_type !== 'agenda' ? a.appointment_type : ''}</span></p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL DÍA */}
      {dayModal && (
        <Modal onClose={() => setDayModal(null)} testId="calendar-day-modal">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#0f766e] font-bold">Citas del día</p>
              <h3 className="mt-1 text-xl font-black capitalize text-slate-900" data-testid="calendar-day-modal-title">{new Date(`${dayModal}T12:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
              <p className="text-sm font-semibold text-slate-700">{dayList.length} citas</p>
            </div>
            <button type="button" onClick={() => setDayModal(null)} className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-bold text-slate-700" data-testid="calendar-day-modal-close">✕</button>
          </div>
          <div className="mt-4 space-y-2">
            {blocked[dayModal] && (
              <div className="flex items-center justify-between gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="calendar-day-blocked-banner">
                <p className="text-sm font-black text-amber-900">🚫 Día bloqueado · {blocked[dayModal].reason}</p>
                {canEdit && <button type="button" onClick={() => unblockDay(blocked[dayModal].id)} className="rounded-full bg-amber-200 px-3 py-1.5 text-xs font-black text-amber-900 hover:bg-amber-300" data-testid="calendar-unblock-day">Desbloquear</button>}
              </div>
            )}
            {dayList.map((a) => <CitaCard key={a.id} a={a} />)}
            {!dayList.length && <p className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-600">{blocked[dayModal] ? 'Día cerrado, sin citas.' : 'Día libre, sin citas.'}</p>}
          </div>
          {canEdit && !blocked[dayModal] && (
            <div className="mt-4 space-y-3">
              <CitaForm initialDate={dayModal} withDate={false} onCreate={onCreate} saving={saving} apiFetch={apiFetch} />
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <select value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800" data-testid="calendar-block-reason">
                  {BLOCK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button type="button" onClick={() => blockDay(dayModal)} className="rounded-xl bg-amber-500 px-3 py-2 text-xs font-black text-white hover:bg-amber-600" data-testid="calendar-block-day">🚫 Bloquear día</button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* MODAL + CITA */}
      {showCita && (
        <Modal onClose={() => setShowCita(false)} testId="calendar-new-cita-modal">
          <div className="flex items-center justify-between"><h3 className="text-lg font-black text-slate-900">Nueva cita</h3><button type="button" onClick={() => setShowCita(false)} className="rounded-full border border-slate-300 px-3 py-1 text-sm font-bold">✕</button></div>
          <div className="mt-3"><CitaForm initialDate={anchorKey} withDate onCreate={onCreate} saving={saving} apiFetch={apiFetch} onDone={() => setShowCita(false)} /></div>
        </Modal>
      )}

      {showPatient && <NewPatientModal apiFetch={apiFetch} onClose={() => setShowPatient(false)} />}
      {showSearch && <SearchCitasModal appointments={appointments} onClose={() => setShowSearch(false)} onJump={(iso) => { const d = new Date(iso); setAnchor(d); setShowSearch(false); openDay(dateKey(d)); }} />}
      {showGoogle && <GoogleSyncModal apiFetch={apiFetch} onClose={() => setShowGoogle(false)} />}
      {showReminders && <RemindersModal apiFetch={apiFetch} onClose={() => setShowReminders(false)} />}
    </section>
  );
}

const NewPatientModal = ({ apiFetch, onClose }) => {
  const [form, setForm] = useState({ patient_name: '', patient_phone: '', patient_email: '', patient_dni: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const res = await apiFetch(`${API}/clinic/patients`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok) setMsg({ ok: true, text: `✅ Paciente ${data.patient.patient_name} creado en el CRM.` });
      else setMsg({ ok: false, text: data.detail || 'No se pudo crear el paciente.' });
    } catch { setMsg({ ok: false, text: 'Error de conexión.' }); }
    setBusy(false);
  };
  return (
    <Modal onClose={onClose} testId="calendar-new-patient-modal">
      <div className="flex items-center justify-between"><h3 className="text-lg font-black text-slate-900">⊕ Nuevo paciente / cliente</h3><button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-3 py-1 text-sm font-bold" data-testid="new-patient-close">✕</button></div>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input value={form.patient_name} onChange={(e) => setForm((p) => ({ ...p, patient_name: e.target.value }))} placeholder="Nombre completo" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="new-patient-name" required />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.patient_phone} onChange={(e) => setForm((p) => ({ ...p, patient_phone: e.target.value }))} placeholder="Teléfono / WhatsApp" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="new-patient-phone" />
          <input value={form.patient_dni} onChange={(e) => setForm((p) => ({ ...p, patient_dni: e.target.value }))} placeholder="DNI" className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="new-patient-dni" />
        </div>
        <input type="email" value={form.patient_email} onChange={(e) => setForm((p) => ({ ...p, patient_email: e.target.value }))} placeholder="Email" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="new-patient-email" />
        {msg && <p className={`rounded-xl px-3 py-2 text-sm font-semibold ${msg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`} data-testid="new-patient-msg">{msg.text}</p>}
        <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#1d4ed8] py-2.5 text-sm font-black text-white disabled:opacity-50" data-testid="new-patient-save">{busy ? 'Guardando…' : 'Crear paciente en CRM'}</button>
      </form>
    </Modal>
  );
};

const SearchCitasModal = ({ appointments, onClose, onJump }) => {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    return appointments.filter((a) => {
      if (!a.scheduled_at) return false;
      const day = a.scheduled_at.slice(0, 10);
      if (from && day < from) return false;
      if (to && day > to) return false;
      if (term && !`${a.patient_name} ${a.notes || ''} ${a.appointment_type || ''}`.toLowerCase().includes(term)) return false;
      return !!term || !!from || !!to;
    }).sort((x, y) => Math.abs(new Date(x.scheduled_at) - Date.now()) - Math.abs(new Date(y.scheduled_at) - Date.now())).slice(0, 60);
  }, [appointments, q, from, to]);
  return (
    <Modal onClose={onClose} testId="calendar-search-modal" maxW="max-w-lg">
      <div className="flex items-center justify-between"><h3 className="text-lg font-black text-slate-900">🔍 Consultar citas</h3><button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-3 py-1 text-sm font-bold" data-testid="search-close">✕</button></div>
      <div className="mt-3 space-y-2">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por paciente, tratamiento o nota…" className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="search-input" />
        <div className="flex gap-2">
          <div className="flex-1"><label className="text-[10px] font-bold uppercase text-slate-500">Desde</label><input type="date" lang="es" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 [color-scheme:light]" data-testid="search-from" /></div>
          <div className="flex-1"><label className="text-[10px] font-bold uppercase text-slate-500">Hasta</label><input type="date" lang="es" value={to} onChange={(e) => setTo(e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 [color-scheme:light]" data-testid="search-to" /></div>
        </div>
      </div>
      <div className="mt-4 space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="search-results">
        {results.map((a) => (
          <button key={a.id} type="button" onClick={() => onJump(a.scheduled_at)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:border-[#1d7fd6]" data-testid={`search-result-${a.id}`}>
            <p className="text-sm font-bold text-slate-900">{a.patient_name}</p>
            <p className="text-xs text-slate-500">{new Date(a.scheduled_at).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {a.appointment_type || 'cita'} · {a.status}</p>
          </button>
        ))}
        {!results.length && (q || from || to) && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">Sin resultados con esos filtros.</p>}
        {!q && !from && !to && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">Escribe un nombre o elige fechas para buscar entre todas las citas.</p>}
      </div>
    </Modal>
  );
};

const GoogleSyncModal = ({ apiFetch, onClose }) => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`${API}/clinic/calendar/google-sync`);
        if (res.ok) setInfo(await res.json());
      } catch { setInfo(null); }
    })();
  }, [apiFetch]);
  return (
    <Modal onClose={onClose} testId="calendar-google-modal" maxW="max-w-lg">
      <div className="flex items-center justify-between"><h3 className="text-lg font-black text-slate-900">🗓 Conectar con Google Calendar</h3><button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-3 py-1 text-sm font-bold" data-testid="google-modal-close">✕</button></div>
      {info ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600">Este calendario se sincroniza con Google Calendar mediante suscripción en vivo (iCal). Cada cita creada o editada aquí aparece automáticamente en el Google Calendar de todo el equipo.</p>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-[11px] font-bold uppercase text-slate-500">URL de suscripción (iCal)</p>
            <p className="mt-1 break-all text-xs text-slate-800" data-testid="google-ics-url">{info.ics_url}</p>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(info.ics_url); setCopied(true); }} className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white" data-testid="google-copy-btn">{copied ? '✅ Copiado' : '📋 Copiar URL'}</button>
          </div>
          <a href={info.google_add_url} target="_blank" rel="noreferrer" className="block rounded-xl bg-[#1d4ed8] px-4 py-3 text-center text-sm font-black text-white hover:bg-[#1e40af]" data-testid="google-add-link">➕ Añadir a mi Google Calendar</a>
          <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-600">
            <li>Pulsa el botón azul (o en Google Calendar: Otros calendarios → + → Desde URL).</li>
            <li>Pega la URL de suscripción y confirma.</li>
            <li>Google actualiza las citas de la clínica automáticamente cada pocas horas.</li>
          </ol>
        </div>
      ) : <p className="mt-4 text-sm text-slate-500">Cargando enlace de sincronización…</p>}
    </Modal>
  );
};

const REM_STATUS_CLS = {
  pendiente: 'bg-amber-100 text-amber-800',
  enviado: 'bg-emerald-100 text-emerald-800',
  sin_email: 'bg-slate-200 text-slate-700',
  error_email: 'bg-rose-100 text-rose-700',
};

const RemindersModal = ({ apiFetch, onClose }) => {
  const [reminders, setReminders] = useState(null);
  const load = React.useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/clinic/scheduled-reminders`);
      setReminders(res.ok ? (await res.json()).reminders || [] : []);
    } catch { setReminders([]); }
  }, [apiFetch]);
  React.useEffect(() => { load(); }, [load]);
  const cancel = async (id) => {
    try {
      const res = await apiFetch(`${API}/clinic/scheduled-reminders/${id}`, { method: 'DELETE' });
      if (res.ok) load();
    } catch { /* noop */ }
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
  return (
    <Modal onClose={onClose} testId="calendar-reminders-modal" maxW="max-w-lg">
      <div className="flex items-center justify-between"><h3 className="text-lg font-black text-slate-900">⏰ Recordatorios programados</h3><button type="button" onClick={onClose} className="rounded-full border border-slate-300 px-3 py-1 text-sm font-bold" data-testid="reminders-close">✕</button></div>
      <p className="mt-1 text-xs text-slate-500">Los emails se envían automáticamente en el día y hora que programes. Si el paciente no tiene email, recibirás un aviso interno para enviarle WhatsApp desde tu número.</p>
      <div className="mt-4 space-y-2 max-h-[55vh] overflow-y-auto" data-testid="reminders-list">
        {reminders === null && <p className="text-sm text-slate-500">Cargando…</p>}
        {reminders?.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5" data-testid={`reminder-row-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900">{r.patient_name}</p>
                <p className="text-xs text-slate-500">Cita: {fmt(r.appointment_at)} · Envío: <span className="font-bold text-slate-700">{fmt(r.send_at)}</span></p>
                {r.message && <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">“{r.message}”</p>}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${REM_STATUS_CLS[r.status] || 'bg-slate-100 text-slate-600'}`}>{r.status}</span>
                {r.status === 'pendiente' && <button type="button" onClick={() => cancel(r.id)} className="rounded-full bg-rose-100 px-2.5 py-1 text-[10px] font-bold text-rose-700" data-testid={`reminder-cancel-${r.id}`}>Cancelar</button>}
              </div>
            </div>
          </div>
        ))}
        {reminders?.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-500">Sin recordatorios programados. Abre una cita y pulsa "⏰ Recordatorio".</p>}
      </div>
    </Modal>
  );
};

export const MiniMonthCalendar = ({ appointments = [] }) => {
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState(null);
  const today = new Date();
  const byDate = useMemo(() => buildByDate(appointments), [appointments]);
  const cells = useMemo(() => {
    const start = startOfWeekSun(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);
  const moveMonth = (dir) => setAnchor((p) => new Date(p.getFullYear(), p.getMonth() + dir, 1));
  const selList = selected ? (byDate[selected] || []) : [];
  return (
    <div data-testid="mini-month-calendar">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => moveMonth(-1)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white font-black hover:bg-white/25" data-testid="mini-cal-prev">‹</button>
        <p className="text-sm font-black capitalize text-white" data-testid="mini-cal-title">{monthTitle(anchor)}</p>
        <div className="flex gap-1">
          <button type="button" onClick={() => { setAnchor(new Date()); setSelected(null); }} className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-white/25" data-testid="mini-cal-today">HOY</button>
          <button type="button" onClick={() => moveMonth(1)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white font-black hover:bg-white/25" data-testid="mini-cal-next">›</button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-0.5 text-center">
        {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map((d, i) => <p key={`${d}${i}`} className="text-[10px] font-black text-cyan-200">{d}</p>)}
        {cells.map((d, i) => {
          const key = dateKey(d);
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const count = (byDate[key] || []).length;
          return (
            <button key={`${key}-${i}`} type="button" onClick={() => setSelected(selected === key ? null : key)}
              className={`relative flex h-9 flex-col items-center justify-center rounded-lg text-[11px] font-bold transition-colors ${selected === key ? 'bg-white text-slate-900' : isToday ? 'bg-cyan-400/30 text-white ring-1 ring-cyan-300' : inMonth ? 'text-white hover:bg-white/10' : 'text-white/25'}`}
              data-testid={`mini-cal-day-${key}`}>
              {d.getDate()}
              {count > 0 && <span className={`absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-0.5 text-[8px] font-black ${selected === key ? 'bg-[#d94a1f] text-white' : 'bg-[#f97316] text-white'}`}>{count}</span>}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="mt-3 rounded-2xl bg-white/10 p-3" data-testid="mini-cal-day-list">
          <p className="text-xs font-black capitalize text-cyan-100">{new Date(`${selected}T12:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} · {selList.length} citas</p>
          <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
            {selList.map((a) => (
              <p key={a.id} className="rounded-lg bg-white/10 px-2 py-1 text-[11px] leading-tight text-white"><span className="font-black">{fmtTime(a._dt)}</span> {a.patient_name}</p>
            ))}
            {!selList.length && <p className="text-[11px] text-cyan-100">Día libre, sin citas.</p>}
          </div>
        </div>
      )}
      <p className="mt-3 text-[10px] text-cyan-200/80">Gestión completa (crear, editar, Google) en la sección Agenda.</p>
    </div>
  );
};
